import { defineStore } from "pinia"
import { fetchApiJson, postApi } from "@/services/repowerAPIService.js"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import useUserStore from "@/stores/userStore.js"

export default defineStore("repower/notificationsListStore", {
  state: () => ({
    notifications: [],
    nextUrl: null,
    count: 0,
    loading: false,
    loadingFailed: false,
  }),
  getters: {
    hasUnseen(state) {
      const seenUntil = useUserStore().notificationsSeenUntil
      if (!seenUntil) {
        return true
      }
      const seenUntilDate = new Date(seenUntil)
      return state.notifications.some(
        (n) => new Date(n.timestamp) > seenUntilDate,
      )
    },
  },
  actions: {
    async loadNotifications() {
      this.loading = true
      this.loadingFailed = false

      try {
        const data = await fetchApiJson("/users/notifications/")
        this.notifications = data.results ?? []
        this.nextUrl = data.next ?? null
        this.count = data.count ?? 0
      } catch (error) {
        this.notifications = []
        this.nextUrl = null
        this.count = 0
        this.loadingFailed = true
        throw error
      } finally {
        this.loading = false
      }
    },

    async refreshNotifications() {
      const latest = this.notifications[0]?.timestamp
      const path = latest
        ? `/users/notifications/?since=${encodeURIComponent(latest)}`
        : "/users/notifications/"

      let data
      try {
        data = await fetchApiJson(path)
      } catch (error) {
        if (error.code === HTTP_STATUS.NOT_MODIFIED) {
          this.loadingFailed = false
          return
        }
        throw error
      }
      const newItems = data.results ?? []

      if (latest) {
        if (newItems.length > 0) {
          this.notifications.unshift(...newItems)
        } else {
          // No new notifications to prepend when polling with a latest timestamp.
        }
      } else {
        this.notifications = newItems
        this.nextUrl = data.next ?? null
      }
      this.count = data.count ?? 0
      this.loadingFailed = false
    },

    async loadMore() {
      if (!this.nextUrl) {
        return
      }

      // Strip scheme+host+/api prefix to get the relative path fetchApiJson expects
      const path = this.nextUrl.replace(/^.*\/api/, "")
      const data = await fetchApiJson(path)
      this.notifications.push(...(data.results ?? []))
      this.nextUrl = data.next ?? null
      this.count = data.count ?? 0
    },

    async markAllAsSeen() {
      try {
        await postApi("/users/seen-notifications/", {})
        useUserStore().notificationsSeenUntil = new Date().toISOString()
      } catch {
        // Not critical — ignore silently
      }
    },
  },
})
